import styles from "./BlogPost.module.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Section } from "../util/section";
import format from "date-fns/format";
import { TinaMarkdown } from "tinacms/dist/rich-text";
import type { Components } from "tinacms/dist/rich-text";
import { tinaField } from "tinacms/dist/react";
import FreeTurnBoard from "./FreeTurnBoard";
import GameViewer from "./GameViewer";
import SimpleGameViewer from "./SimpleGameViewer";
import DisplayBoard from "./DisplayBoard";
import ChallengeBoard from "./ChallengeBoard";
import LessonBoard from "./LessonBoard";
import SvgViewer from "./SvgViewer";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import { FaBook } from "react-icons/fa";
import { cn } from "@/lib/utils";

const TITLE_LENGTH_THRESHOLD = 30;
const READING_WORDS_PER_MINUTE = 400;

function countWordsInRichText(node: any): number {
  if (!node) {
    return 0;
  }

  if (Array.isArray(node)) {
    return node.reduce(
      (total, child) => total + countWordsInRichText(child),
      0,
    );
  }

  if (typeof node !== "object") {
    return 0;
  }

  return Object.entries(node).reduce((total, [key, value]) => {
    if (key === "text" && typeof value === "string") {
      const words = value.trim().split(/\s+/).filter(Boolean);
      return total + words.length;
    }

    return total + countWordsInRichText(value);
  }, 0);
}

export default function Post(props: any) {
  const [activeId, setActiveId] = useState(null);
  const [progress, setProgress] = useState("0%");
  // const [useCompactTitle, setUseCompactTitle] = useState(false);
  const sectionRefs = useRef({});
  const { t } = useTranslation("common");

  const headings = props._body.children
    .filter(
      (node) =>
        node.children[0].text != null && node.type.substring(0, 2) == "h2",
    )
    .map((node) => {
      return {
        id: node.children[0].text.replace(/ /g, "-"),
        text: node.children[0].text,
      };
    });

  function findPrevSectionId(id) {
    const index = headings.findIndex((item) => item.id == id);
    return index > 0 ? headings[index - 1].id : id;
  }

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const observer = new IntersectionObserver(
      (entries) => {
        let nextActiveId: string | null = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting && window.scrollY >= lastScrollY) {
            nextActiveId = entry.target.id;
          } else if (
            !entry.isIntersecting &&
            window.scrollY < lastScrollY &&
            entry.target.id !== headings[0]?.id
          ) {
            const prevId = findPrevSectionId(entry.target.id);
            if (prevId) nextActiveId = prevId;
          }
        });
        if (nextActiveId) {
          setActiveId(nextActiveId);
        }
        lastScrollY = window.scrollY;
      },
      { rootMargin: "-20% 0px -68% 0px", threshold: [0, 0.5, 0.1] },
    );

    Object.values(sectionRefs.current).forEach((ref: Element) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const sidebarTopOffset = 364;

    const updateScrollProgress = () => {
      const scrollTop = document.documentElement.scrollTop;
      const scrollStart = scrollTop > sidebarTopOffset;
      if (scrollStart) {
        const scrollHeight =
          document.documentElement.scrollHeight -
          document.documentElement.clientHeight -
          sidebarTopOffset;
        const scrollProgress = `${
          ((scrollTop - sidebarTopOffset) / scrollHeight) * 100
        }%`;
        setProgress(scrollProgress);
      } else {
        setProgress("0%");
      }
    };

    window.addEventListener("scroll", updateScrollProgress);
    return () => {
      window.removeEventListener("scroll", updateScrollProgress);
    };
  }, []);

  // useEffect(() => {
  //   const titleLength = props?.title?.trim().length ?? 0;
  //   setUseCompactTitle(titleLength > TITLE_LENGTH_THRESHOLD);
  // }, [props.title]);

  const components: Components<{
    FreeTurnBoard: any;
    DisplayBoard: any;
    ChallengeBoard: any;
    GameViewer: any;
    SimpleGameViewer: any;
    LessonBoard: any;
    SvgViewer: any;
  }> = {
    img: (props) => (
      <span className="flex items-center justify-start w-full overflow-x-visible">
        <img src={props.url} alt={props.alt} />
      </span>
    ),
    h2: (props) => {
      const text = props.children.props.content[0].text;
      const id = text.replace(/ /g, "-");

      return (
        <h2
          id={id}
          ref={(el) => {
            sectionRefs.current[id] = el;
          }}
        >
          {text}
        </h2>
      );
    },
    table: (props) => {
      const { children } = props as any;
      return (
        <div
          className={`not-prose my-8 rounded ring-1 ring-slate-200 ${styles.table}`}
        >
          <div className="overflow-hidden rounded">
            <table className="min-w-full text-sm border-separate border-spacing-0">
              {children}
            </table>
          </div>
        </div>
      );
    },
    FreeTurnBoard,
    DisplayBoard,
    ChallengeBoard,
    GameViewer,
    SimpleGameViewer,
    LessonBoard,
    SvgViewer,
  };

  const date = new Date(props.date);
  let formattedDate = "";
  if (!isNaN(date.getTime())) {
    formattedDate = format(date, "MMMM dd, yyyy");
  }
  const wordCount = useMemo(
    () => countWordsInRichText(props._body),
    [props._body],
  );
  const readTimeMinutes = Math.max(
    1,
    Math.ceil(wordCount / READING_WORDS_PER_MINUTE),
  );

  // console.log("props._body", props._body);
  // console.log("props.author", props.author);

  return (
    <Section className="flex-1 bg-white">
      <div
        className={`flex flex-col w-full justify-center items-center min-h-[150px] py-12
      bg-altblue-600  ${styles.post}`}
      >
        <h5
          data-tina-field={tinaField(props.category, "category")}
          className="flex items-center mb-1 space-x-1 text-lg font-semibold text-center text-altblue-200"
        >
          <FaBook />
          <Link
            className="uppercase hover:text-altblue-200"
            href={`/blog/category/${props.category.slug}`}
            locale={false}
          >
            {t(props.category.category)}
          </Link>
        </h5>
        <div className="flex flex-wrap items-center justify-center pt-4 text-xs font-medium md:text-sm text-altblue-200">
          {props.author?.name && (
            <>
              <span>
                By{" "}
                <Link
                  data-tina-field={tinaField(props.author, "name")}
                  className=" text-altblue-200 hover:text-white"
                  href={`/blog/author/${props.author._sys.filename}`}
                  locale={false}
                >
                  {props.author.name}
                </Link>
              </span>
              <span className="px-2"> · </span>
            </>
          )}
          {formattedDate && (
            <>
              <span data-tina-field={tinaField(props, "date")}>
                {formattedDate}
              </span>
              <span className="px-2">·</span>
            </>
          )}
          <span>{readTimeMinutes} min read</span>
        </div>
      </div>

      <div className="flex flex-col items-center">
        <div
          id="reading-progress-bar"
          className="bg-blue-600"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: progress,
            height: "5px",
            zIndex: "9999",
            transition: "width 0.3s ease-out",
          }}
        ></div>

        <div className="w-full max-w-[1360px] mx-auto px-6 py-12 md:grid md:grid-cols-[180px_minmax(0,1fr)] md:gap-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8 xl:grid-cols-[240px_750px_minmax(0,1fr)] xl:gap-12">
          <aside className="hidden md:block md:col-start-1">
            <div className="sticky top-6">
              <p className="pl-3 mb-3 text-sm font-semibold text-altgrey-700">
                Contents
              </p>
              <nav className="space-y-1">
                {headings.map((heading) => {
                  return (
                    <Link
                      className={cn(
                        "block rounded px-3 py-1 text-sm transition-colors hover:text-gray-900",
                        activeId === heading.id
                          ? "bg-gray-200 text-blue-600"
                          : "text-gray-600",
                      )}
                      href={`#${heading.id}`}
                      key={heading.id}
                    >
                      {heading.text}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </aside>

          <article className="w-full max-w-[750px] mx-auto md:col-start-2 md:mx-0 xl:w-[750px] xl:max-w-none xl:justify-self-start">
            <h1
              data-tina-field={tinaField(props, "title")}
              className={cn(
                "text-3xl font-extrabold tracking-normal prose text-left title-font md:text-4xl",
                props.subtitle ? "mb-2" : "mb-8",
              )}
            >
              {props.title}
            </h1>
            {props.subtitle && (
              <p
                className={cn(
                  "mb-8 text-left text-gray-500 text-xl md:text-2xl",
                )}
                data-tina-field={tinaField(props, "subtitle")}
              >
                {props.subtitle}
              </p>
            )}
            {props.heroImg && (
              <div
                data-tina-field={tinaField(props, "heroImg")}
                className="relative mb-10"
              >
                <img
                  src={props.heroImg}
                  alt={props.title}
                  className="block w-full h-auto rounded-lg"
                />
              </div>
            )}
            <div
              data-tina-field={tinaField(props, "_body")}
              className="w-full prose max-w-none dark:prose-dark"
            >
              <TinaMarkdown components={components} content={props._body} />

              <div className={"h-96 "}> </div>
            </div>
          </article>
        </div>
      </div>
    </Section>
  );
}
